import React, { useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState, useSetRecoilState, useRecoilCallback } from 'recoil';
import { Stop } from '@app/shared/models/tour.type';
import { MapCard } from '@app/new-app/MapCard';
import { LockedStopOverlay } from './LockedStopOverlay';
import { calculateDemoStopLimit } from './utils';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { selector_visibleStopIds } from '@app/shared/state/filter.atom';

// Thin wrapper: only re-renders when this specific stop's visibility changes
const CardVisibility = React.memo(function CardVisibility({
  stopId,
  children,
}: {
  stopId: string;
  children: React.ReactNode;
}) {
  const visibleStopIds = useRecoilValue(selector_visibleStopIds);
  const isVisible = visibleStopIds.has(stopId);
  return <div style={{ display: isVisible ? 'contents' : 'none' }}>{children}</div>;
});
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { useRenderTracker } from '@app/shared/debug';

interface StopCardListProps {
  mode: 'mobile' | 'desktop';
  isHebrew: () => boolean;
  preventMapInteraction: (e: React.MouseEvent | React.TouchEvent) => void;
  isListMode: boolean;
  mapref?: React.RefObject<any>;
  flyToSelectedStop?: (stopId: string) => void;
  visibleStopIndices?: { start: number; end: number } | null;
}

export const StopCardList = React.memo<StopCardListProps>(function StopCardList({
  mode,
  isHebrew,
  preventMapInteraction,
  isListMode,
  mapref,
  flyToSelectedStop,
  visibleStopIndices = null
}) {
  const { t } = useTranslation();
  const tourState = useRecoilValue(atom_tourState);
  const [activeStopState, setActiveStopState] = useRecoilState(atom_activeStopState);
  const activeStopId = activeStopState?.id;
  const setOpenedStopState = useSetRecoilState(atom_openedStopState);
  const setShowModal = useSetRecoilState(atom_showModalContent);
  const setModalType = useSetRecoilState(atom_modalType);

  useRenderTracker('StopCardList');
  const stops = tourState?.stops || [];
  const demoStop = tourState?.config.demo_stop;
  const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
  const isDemo = uID?.toLowerCase() === 'demo';

  // Refs so the callback doesn't need these as dependencies
  const activeStopIdRef = useRef(activeStopId);
  activeStopIdRef.current = activeStopId;
  const getOpenedStop = useRecoilCallback(({ snapshot }) => () =>
    snapshot.getLoadable(atom_openedStopState).getValue()
  , []);

  // One stable callback for all cards
  const handleStopSelect = useCallback((stop: Stop) => {
    // If BigStopPage is open with a different stop, reset it first
    const currentOpenedStop = getOpenedStop();
    if (currentOpenedStop?.id && currentOpenedStop.id !== stop.id) {
      setOpenedStopState(undefined);
      setActiveStopState(stop);
      setTimeout(() => {
        setOpenedStopState(stop);
      }, 0);
      return;
    }

    setActiveStopState(stop);
    if (mapref?.current) {
      const mapCenter = mapref.current.getCenter();
      const stopLat = stop.stop_location?.latitude;
      const stopLng = stop.stop_location?.longitude;
      const precision = Math.pow(10, -(mapref.current.getZoom() - 15));
      if (
        activeStopIdRef.current === stop.id &&
        (Math.abs(mapCenter.lat - stopLat) < 0.003 * precision &&
          Math.abs(mapCenter.lng - stopLng) < 0.003 * precision) &&
        mapref.current.getZoom() >= 16
      ) {
        setShowModal(true);
      } else if (activeStopIdRef.current === stop.id && flyToSelectedStop) {
        flyToSelectedStop(stop.id);
      } else {
        setActiveStopState(stop);
      }
    }
  }, [mapref, flyToSelectedStop, setActiveStopState, setShowModal, setOpenedStopState]);

  // Build cards from stops
  const cards = useMemo(() => stops.map((stop: Stop) => ({
    id: stop.id,
    title: stop.s_title,
    description: stop.s_smallDesc,
    image: stop.loc_pics?.[0]?.url?.split('&token=')?.[0],
    categories: stop.tags?.map((category: any) => category.text) || [],
    rating: stop?.rating,
    isSelected: activeStopId === stop.id,
    minimal: false,
    stop: stop,
    show_on_list: stop.show_on_slider
  })), [stops, activeStopId]);

  const renderedCards = useMemo(() => {
    // During resize: show only 3 visible cards (frozen - no re-renders)
    if (visibleStopIndices && mode === 'mobile' && isListMode) {
      const { start, end } = visibleStopIndices;
      const visibleCards = cards.slice(start, Math.min(end, cards.length));

      return visibleCards.map((card, index) => {
        const actualIndex = start + index;
        const numOfStops = calculateDemoStopLimit(isDemo, demoStop);
        const isLocked = isDemo && (numOfStops === -1 ? true : (typeof numOfStops === 'number' ? actualIndex > numOfStops : false));

        return (
          <article
            style={{
              margin: index === 0 ? '0px 2px' : '10px 2px',
              position: isLocked ? 'relative' : undefined
            }}
            key={card.id + actualIndex}
            id={card.id}
          >
            <div
              style={{
                filter: isLocked ? 'blur(4px)' : 'none',
                pointerEvents: isLocked ? 'none' : 'auto'
              }}
            >
              <MapCard
                key={card.id}
                title={card.title}
                description={card.description}
                activeStop={card.stop}
                image={card.image}
                categories={card.categories}
                rating={card.rating}
                onSelect={handleStopSelect}
                isSelected={card.isSelected}
                minimal={card.minimal}
                isListMode={isListMode}
              />
            </div>
            {isLocked && (
              <LockedStopOverlay
                isHebrew={isHebrew()}
                onPurchaseClick={() => setModalType('purchaseModalFromDemo')}
              />
            )}
          </article>
        );
      });
    }

    // Render ALL stops, wrap each in CardVisibility for show/hide without remount
    const numOfStops = calculateDemoStopLimit(isDemo, demoStop);

    return cards
      .filter((card: any) => (card.show_on_list === undefined ? true : card.show_on_list))
      .map((card, index) => {
      const isLocked = isDemo && (numOfStops === -1 ? true : (typeof numOfStops === 'number' ? index > numOfStops : false));

      return (
        <CardVisibility key={card.id} stopId={card.id}>
          <article
            style={{
              margin: index === 0 ? '0px 2px' : '10px 2px',
              position: isLocked ? 'relative' : undefined,
              direction: isHebrew() ? 'rtl' : 'ltr',
            }}
            id={card.id}
            onClick={preventMapInteraction}
            onTouchStart={preventMapInteraction}
            onTouchMove={preventMapInteraction}
            onMouseDown={preventMapInteraction}
            aria-label={t('accessibility.stop_card', { title: card.title }) as string}
          >
            <div
              style={{
                filter: isLocked ? 'blur(4px)' : 'none',
                pointerEvents: isLocked ? 'none' : 'auto'
              }}
            >
              <MapCard
                key={card.id}
                title={card.title}
                description={card.description}
                activeStop={card.stop}
                image={card.image}
                categories={card.categories}
                rating={card.rating}
                onSelect={handleStopSelect}
                isSelected={card.isSelected}
                minimal={card.minimal}
                isListMode={isListMode}
              />
            </div>
            {isLocked && (
              <LockedStopOverlay
                isHebrew={isHebrew()}
                onPurchaseClick={() => setModalType('purchaseModalFromDemo')}
              />
            )}
          </article>
        </CardVisibility>
      );
    });
  }, [cards, mode, isDemo, demoStop, t, isHebrew, preventMapInteraction, setModalType, isListMode, visibleStopIndices, handleStopSelect]);

  return <>{renderedCards}</>;
});
